import { useGetCategories } from '@agos/api-client/menu'
import { totalItems, useCart, useCustomerAuth } from '@agos/customer-core'
import { T, tt } from '@agos/shared'
import {
  ChevronRight,
  FileText,
  Home,
  Info,
  LogIn,
  LogOut,
  MoreHorizontal,
  Phone,
  ShoppingBag,
  User,
  UserPlus,
  UtensilsCrossed,
  X,
} from 'lucide-react'
import { useState } from 'react'
import { Link, useLocation } from 'wouter'

import { useRestaurantInfo } from '@agos/customer-core'

/**
 * The phone navigation — five tabs fixed to the bottom, and the sheets three of them open.
 *
 * `md:hidden`, as in legacy: on a desktop the header carries all of this. The first AGOS draft shipped a
 * three-item bar that was visible at every width, which is both less than legacy has and in the way of a
 * layout that never wanted it.
 *
 * The category tab scrolls the menu on the home page rather than navigating, because the menu *is* the
 * home page — `data-category` is the handle, and pressing a category from another route goes home first.
 */

type Sheet = 'menu' | 'profile' | 'more' | null

export function BottomNav({ cmsPages }: { cmsPages: Array<{ id: number; slug: string; title: string }> }) {
  const [location, navigate] = useLocation()
  const lines = useCart((state) => state.lines)
  const count = totalItems(lines)
  const user = useCustomerAuth((state) => state.user)
  const signOut = useCustomerAuth((state) => state.signOut)
  const info = useRestaurantInfo()
  const [sheet, setSheet] = useState<Sheet>(null)
  const { data } = useGetCategories()

  const categories = (Array.isArray(data) ? data : []).filter(
    (row): row is { id: number; name: string; visibleOnWeb?: boolean } =>
      typeof row === 'object' && row !== null && (row as { visibleOnWeb?: boolean }).visibleOnWeb !== false,
  )
  const isHome = location === '/' || location === '/menu'

  function close() {
    setSheet(null)
  }

  function toggle(next: Exclude<Sheet, null>) {
    setSheet((current) => (current === next ? null : next))
  }

  function goHome() {
    close()
    if (!isHome) navigate('/')
    // The menu is on the home page, so "home" from the home page means the top of it.
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  function selectCategory(id: number) {
    close()
    const press = () => document.querySelector<HTMLElement>(`[data-category="${id}"]`)?.click()
    if (isHome) {
      press()
      return
    }
    navigate('/')
    // One frame for the route to mount before the chip exists to press.
    requestAnimationFrame(press)
  }

  const profileLabel = user === null ? tt('Prihlásiť') : ((user.name ?? '').split(' ')[0] || tt('Profil'))

  return (
    <>
      {sheet !== null && (
        <button
          type="button"
          aria-label={tt('Zavrieť')}
          onClick={close}
          className="fixed inset-0 z-40 bg-black/40 md:hidden"
          data-sheet-overlay
        />
      )}

      {sheet === 'menu' && (
        <Sheet title={tt('Ponuka')} onClose={close} name="menu" className="max-h-[70vh] flex flex-col">
          <div className="px-4 pb-6 space-y-1 overflow-y-auto flex-1">
            {categories.map((category) => (
              <button
                key={category.id}
                type="button"
                onClick={() => selectCategory(category.id)}
                data-sheet-category={category.id}
                className="w-full flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-secondary active:bg-secondary/80 transition-colors text-left min-h-[44px]"
              >
                <UtensilsCrossed className="w-5 h-5 text-primary flex-shrink-0" />
                <span className="font-medium flex-1">{category.name}</span>
                <ChevronRight className="w-4 h-4 text-muted-foreground" />
              </button>
            ))}
            {categories.length === 0 && (
              <div className="rounded-xl border border-border p-4 text-center text-muted-foreground text-sm">
                <T>Žiadne kategórie</T>
              </div>
            )}
          </div>
        </Sheet>
      )}

      {sheet === 'profile' && user === null && (
        <Sheet title={tt('Môj profil')} onClose={close} name="profile">
          <div className="px-4 pb-6 space-y-3">
            <div className="flex items-center gap-3 py-3 border-b border-border">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                <User className="w-6 h-6 text-primary" />
              </div>
              <div>
                <p className="font-medium text-foreground">
                  <T>Hosť</T>
                </p>
                <p className="text-sm text-muted-foreground">
                  <T>Nie ste prihlásený</T>
                </p>
              </div>
            </div>
            <Link
              href="/prihlasenie"
              onClick={close}
              className="flex items-center gap-3 px-3 py-3 rounded-xl bg-primary/5 border border-primary/20 hover:bg-primary/10 transition-colors"
            >
              <LogIn className="w-5 h-5 text-primary flex-shrink-0" />
              <div className="flex-1">
                <p className="font-medium text-sm text-foreground">
                  <T>Prihlásiť sa</T>
                </p>
                <p className="text-xs text-muted-foreground">
                  <T>Zobraziť profil a históriu objednávok</T>
                </p>
              </div>
              <ChevronRight className="w-4 h-4 text-muted-foreground" />
            </Link>
            <Link
              href="/registracia"
              onClick={close}
              className="flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-secondary transition-colors"
            >
              <UserPlus className="w-5 h-5 text-primary flex-shrink-0" />
              <span className="font-medium text-sm">
                <T>Zaregistrovať sa</T>
              </span>
              <ChevronRight className="w-4 h-4 text-muted-foreground ml-auto" />
            </Link>
          </div>
        </Sheet>
      )}

      {sheet === 'more' && (
        <Sheet title={tt('Ďalšie')} onClose={close} name="more">
          <div className="px-4 pb-6 space-y-1">
            {info.phone !== null && (
              <a
                href={`tel:${info.phone.replace(/\s/g, '')}`}
                className="flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-secondary transition-colors"
              >
                <Phone className="w-5 h-5 text-primary" />
                <span className="font-medium">{info.phone}</span>
              </a>
            )}
            {info.address !== null && (
              <div className="flex items-center gap-3 px-3 py-3 text-sm text-muted-foreground">
                <Info className="w-5 h-5 text-primary flex-shrink-0" />
                <span>{info.address}</span>
              </div>
            )}
            {cmsPages.map((page) => (
              <Link
                key={page.id}
                href={`/page/${page.slug}`}
                onClick={close}
                className="flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-secondary transition-colors"
              >
                <FileText className="w-5 h-5 text-primary" />
                <span className="font-medium">{page.title}</span>
              </Link>
            ))}
            {user !== null && (
              <button
                type="button"
                data-sheet-sign-out
                onClick={() => {
                  signOut()
                  close()
                }}
                className="w-full flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-secondary transition-colors text-left"
              >
                <LogOut className="w-5 h-5 text-destructive" />
                <span className="font-medium">
                  <T>Odhlásiť sa</T>
                </span>
              </button>
            )}
          </div>
        </Sheet>
      )}

      <nav
        className="fixed bottom-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-xl border-t border-border md:hidden"
        data-bottom-nav
        aria-label={tt('Hlavná navigácia')}
      >
        <div className="flex items-stretch h-16">
          <Tab icon={Home} label={tt('Domov')} name="home" active={isHome && sheet === null} onPress={goHome} />
          <Tab
            icon={UtensilsCrossed}
            label={tt('Ponuka')}
            name="menu"
            active={sheet === 'menu'}
            onPress={() => toggle('menu')}
          />
          <Tab
            icon={ShoppingBag}
            label={tt('Košík')}
            name="cart"
            href="/checkout"
            badge={count}
            active={location === '/checkout'}
          />
          <Tab
            icon={User}
            label={profileLabel}
            name="profile"
            active={sheet === 'profile'}
            onPress={() => {
              if (user === null) toggle('profile')
              else navigate('/moj-profil')
            }}
          />
          <Tab
            icon={MoreHorizontal}
            label={tt('Ďalšie')}
            name="more"
            active={sheet === 'more'}
            onPress={() => toggle('more')}
          />
        </div>
      </nav>
    </>
  )
}

function Tab(props: {
  icon: typeof Home
  label: string
  name: string
  active: boolean
  href?: string
  badge?: number
  onPress?: () => void
}) {
  const Icon = props.icon
  const className = `flex-1 flex items-center justify-center transition-colors active:opacity-70 ${
    props.active ? 'text-primary' : 'text-muted-foreground hover:text-foreground'
  }`
  const content = (
    <div className="flex flex-col items-center justify-center gap-0.5">
      <div className="relative">
        <Icon className="w-6 h-6" />
        {props.badge !== undefined && props.badge > 0 && (
          <span
            data-nav-badge
            className="absolute -top-1.5 -right-1.5 bg-primary text-primary-foreground text-xs font-bold min-w-[18px] h-[18px] rounded-full flex items-center justify-center px-0.5 border-2 border-background"
          >
            {props.badge > 99 ? '99+' : props.badge}
          </span>
        )}
      </div>
      <span className="text-xs font-medium leading-none truncate max-w-[56px]">{props.label}</span>
    </div>
  )

  if (props.href !== undefined) {
    return (
      <Link
        href={props.href}
        data-nav={props.name}
        aria-current={props.active ? 'page' : undefined}
        className={className}
      >
        {content}
      </Link>
    )
  }
  return (
    <button
      type="button"
      data-nav={props.name}
      aria-current={props.active ? 'page' : undefined}
      onClick={props.onPress}
      className={className}
    >
      {content}
    </button>
  )
}

function Sheet(props: {
  title: string
  name: string
  onClose: () => void
  className?: string
  children: React.ReactNode
}) {
  return (
    <div
      data-sheet={props.name}
      className={`fixed bottom-16 left-0 right-0 z-50 bg-background rounded-t-2xl shadow-2xl border-t border-border md:hidden ${props.className ?? ''}`}
    >
      <div className="flex items-center justify-between px-4 pt-4 pb-2 flex-shrink-0">
        <h2 className="font-semibold text-lg">{props.title}</h2>
        <button
          type="button"
          onClick={props.onClose}
          aria-label={tt('Zavrieť')}
          className="p-2.5 rounded-full hover:bg-secondary active:bg-secondary/80 transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
        >
          <X className="w-5 h-5" />
        </button>
      </div>
      {props.children}
    </div>
  )
}
